import type { AdditionalParams, ApiCallResult } from '../util/apiClient';
import { apiPut, apiPost, apiGet, apiDelete } from '../util/http';
import { getPath } from '../util/getPath';
import type { CreateItemUploadLockRequestDto, CreateItemUploadLockResponseDto, CreateItemUploadPartsSignedUrlsRequestDto, CreateItemUploadPartsSignedUrlsResponseDto, GetItemUploadLockResponseDto, UploadLocationId, UploadPartDto } from '../dto/uploadLocationDto';
import type { CancelToken } from 'axios';
import type { TaskPriority } from '../async/taskPriority';
import type { BackendClient } from '../client/backendClient';
import { assumeResultObject } from '../client/assumeResult';

export const ofItemUpload = (sl: { itemUpload: string }, itemId: string) => getPath(sl.itemUpload, { itemId });

export const ofItemUploadPartsSignedUrls = (sl: { itemUploadPartsSignUrls: string }, itemId: string) => getPath(sl.itemUploadPartsSignUrls, { itemId });

export const ofItemMultipartUploadComplete = (sl: { itemMultipartUploadComplete: string }, itemId: string) => getPath(sl.itemMultipartUploadComplete, { itemId });

export function createItemUploadLock(c: BackendClient, itemId: string, request: CreateItemUploadLockRequestDto, options?: AdditionalParams): Promise<CreateItemUploadLockResponseDto> {
  return apiPut(c.getApiClient(), ofItemUpload(c.getServiceLinks(), itemId), request, options).then((result) => assumeResultObject<CreateItemUploadLockResponseDto>(result, 'uploadLocationId'));
}

export const createItemUploadPartsSignedUrls = async (
  client: BackendClient,
  itemId: string,
  request: CreateItemUploadPartsSignedUrlsRequestDto,
  cancelToken?: CancelToken,
  priority?: TaskPriority
): Promise<CreateItemUploadPartsSignedUrlsResponseDto> => {
  // Force refresh of session to prevent 401
  await client.getApiClient().refreshAuth();
  return apiPost(
    client.getApiClient(),
    ofItemUploadPartsSignedUrls(client.getServiceLinks(), itemId),
    request,
    { cancelToken, priority }
  ).then((response) => assumeResultObject<CreateItemUploadPartsSignedUrlsResponseDto>(response));
};


export function requestCreateItemUploadLock(
  c: BackendClient,
  maxChunkSize: number,
  multipartThreshold: number,
  itemId: string,
  uploadLocationId: UploadLocationId,
  fileSize: number,
  contentType: string,
  targetFolder: string | undefined,
  lockOwnerInstanceId: string
): Promise<CreateItemUploadLockResponseDto> {
  const request: CreateItemUploadLockRequestDto = {
    lockOwnerInstanceId,
    uploadLocationId,
    fileSize,
    contentType,
    targetFolder,
    chunkSize: maxChunkSize,
    isMultiPartUpload: fileSize >= multipartThreshold,
  };

  return createItemUploadLock(c, itemId, request);
}

export function completeMultipartUpload(
  client: BackendClient,
  itemId: string,
  uploadLocationId: UploadLocationId,
  targetKey: string,
  uploadId: string,
  isCanceled: boolean,
  uploadParts: UploadPartDto[],
  options?: AdditionalParams
) {
  return apiPost(client.getApiClient(), ofItemMultipartUploadComplete(client.getServiceLinks(), itemId), {
    uploadLocationId,
    targetKey,
    uploadId,
    isCanceled,
    uploadParts,
  }, options);
}

/**
 * Item upload
 */
export const api_getItemUploadLock = async (c: BackendClient, itemId: string): Promise<GetItemUploadLockResponseDto> =>
  apiGet(c.getApiClient(), ofItemUpload(c.getServiceLinks(), itemId)).then((result) => assumeResultObject<GetItemUploadLockResponseDto>(result, 'isUploadLocked'));


export const api_createItemUploadLock = async (c: BackendClient, itemId: string, request: CreateItemUploadLockRequestDto): Promise<CreateItemUploadLockResponseDto> =>
  createItemUploadLock(c, itemId, request);


export const api_deleteItemUploadLock = (c: BackendClient, itemId: string, options?: AdditionalParams): Promise<ApiCallResult> =>
  apiDelete(c.getApiClient(), ofItemUpload(c.getServiceLinks(), itemId), {}, options);


export const api_createItemUploadPartsSignedUrls = (c: BackendClient, itemId: string, request: CreateItemUploadPartsSignedUrlsRequestDto): Promise<CreateItemUploadPartsSignedUrlsResponseDto> =>
  createItemUploadPartsSignedUrls(c, itemId, request);


export const api_completeMultipartUpload = (c: BackendClient, itemId: string, uploadLocationId: UploadLocationId, targetKey: string, uploadId: string, isCanceled: boolean, uploadParts: UploadPartDto[]) =>
  completeMultipartUpload(c, itemId, uploadLocationId, targetKey, uploadId, isCanceled, uploadParts);

